// --- Shared types ---

import type { PlanDTO } from '../../../Generated/PlanDTO.ts'

export type UserType = 'lab' | 'customer'

// --- Static content data (from spcfy.io pricing widget) ---

export const PLAN_CONTENT: Record<
  UserType,
  {
    base: { name: string; desc: string; features: { text: string; tip?: string }[] }
    insight: { name: string; desc: string; features: { text: string; tip?: string }[] }
  }
> = {
  lab: {
    base: {
      name: 'spcfyBASE',
      desc: 'Everything you need to run your eDNA lab',
      features: [
        { text: 'Separate billing per sample' },
        { text: 'Create comprehensive reports' },
        { text: 'Manage orders and files' },
        { text: 'Reruns', tip: 'Re-process samples with updated parameters' },
        { text: 'Secure hosting of your data' }
      ]
    },
    insight: {
      name: 'spcfyINSIGHT',
      desc: 'Advanced analytics and marketplace access',
      features: [
        { text: 'All from spcfyBASE' },
        { text: 'Marketplace Access', tip: 'Get discovered by customers looking for eDNA services' },
        { text: 'Upgrade reporting' },
        { text: 'Share interactive project views' },
        { text: 'Advanced taxonomy filters' }
      ]
    }
  },
  customer: {
    base: {
      name: 'spcfyBASE',
      desc: 'Access and manage your eDNA results',
      features: [
        { text: 'Centralized data access' },
        { text: 'Transparent reproducible results' },
        { text: 'Full data ownership' },
        { text: 'Order and file management' },
        { text: 'Ready-to-share Reports' }
      ]
    },
    insight: {
      name: 'spcfyINSIGHT',
      desc: 'Deep analysis and interactive exploration',
      features: [
        { text: 'All from spcfyBASE' },
        { text: 'Organize datasets in projects' },
        { text: 'Interactive visualizations' },
        { text: 'Advanced taxonomy filters & groupings' },
        { text: 'Project Sharing' }
      ]
    }
  }
}

// --- Helpers ---

import { parseTier } from '../../../Util/billing.js'
import type { CreditItemDTO } from '../../../Generated/CreditItemDTO.ts'
import type { CreditItemTierDTO } from '../../../Generated/CreditItemTierDTO.ts'

export { parseTier }

export function findPlan(
  plans: PlanDTO[],
  orgType: string,
  commercialType: string,
  billingPeriod: string,
  tier: string
): PlanDTO | undefined {
  const exact = plans.find(
    (p) => p.orgType === orgType && p.commercialType === commercialType && p.billingPeriodUnit === billingPeriod && parseTier(p.id) === tier
  )
  if (exact) return exact
  return plans.find((p) => p.orgType === orgType && p.commercialType === commercialType && parseTier(p.id) === tier)
}

export function formatPrice(cents: number, currency: string): string {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100)
}

export function formatPricePerSample(cents: number, currency: string): string {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(
    cents / 100
  )
}

// --- Pricing calculation ---

export function calculatePrice(item: CreditItemDTO, quantity: number): number {
  if (quantity <= 0) return 0
  if ((item.tiers?.length ?? 0) > 0) {
    if (item.pricingModel === 'volume') {
      return calculateVolumePrice(item.tiers ?? [], quantity)
    }

    return calculateTieredPrice(item.tiers ?? [], quantity)
  }
  return item.priceInCents * quantity
}

/** Graduated/tiered: different rates apply to different slices of the quantity */
function calculateTieredPrice(tiers: CreditItemTierDTO[], quantity: number): number {
  let remaining = quantity
  let total = 0
  for (const tier of tiers) {
    if (remaining <= 0) break
    const tierCapacity = tier.endingUnit != null ? tier.endingUnit - tier.startingUnit + 1 : remaining
    const units = Math.min(remaining, tierCapacity)
    total += units * tier.priceInCents
    remaining -= units
  }
  return total
}

function calculateVolumePrice(tiers: CreditItemTierDTO[], quantity: number): number {
  let applicableTier = tiers[0]
  for (const tier of tiers) {
    if (quantity >= tier.startingUnit) {
      applicableTier = tier
    }
  }
  return quantity * applicableTier.priceInCents
}

export const MAX_SAMPLES = 20_000
export const SLIDER_STEPS = 1000
const LOG_RANGE = Math.log(MAX_SAMPLES)

export function sliderToSamples(val: number): number {
  if (val <= 0) return 0
  const res = Math.round(Math.exp((val / SLIDER_STEPS) * LOG_RANGE))
  return res > 1000 ? Math.round(res / 100) * 100 : res
}

export function samplesToSlider(samples: number): number {
  if (samples <= 0) return 0
  return Math.min(SLIDER_STEPS, Math.max(0, (Math.log(samples) / LOG_RANGE) * SLIDER_STEPS))
}
